// frontend/src/components/integrations/integration-categories.ts
import { AlertTriangle, Bot, ClipboardList, Cloud, Code2, Database, FileCheck, LineChart, MessageCircleMore, Server, Shield, Workflow } from "lucide-react";
import type { IntegrationType } from "src/types/integrations";

export type { IntegrationType };

export type IntegrationCategoryId =
  | "telemetry"
  | "source-control"
  | "chat"
  | "alert-response"
  | "documentation"
  | "cicd-workflows"
  | "project-management"
  | "cloud-infrastructure"
  | "data-warehouses"
  | "security"
  | "coding-agents"
  | "mcp-servers";

export interface IntegrationCategory {
  id: IntegrationCategoryId;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  integrationTypes: IntegrationType[];
  urlSlug: string;
}

// Base categories without descriptions (descriptions and some titles are generated dynamically based on brand)
const BASE_INTEGRATION_CATEGORIES: Omit<IntegrationCategory, "description" | "title">[] = [
  {
    id: "telemetry",
    icon: LineChart,
    integrationTypes: ["DATADOG", "NEW_RELIC", "GRAFANA_CLOUD", "GCP_CLOUD_MONITORING", "ROLLBAR", "SENTRY", "LANGSMITH", "LANGFUSE", "PGANALYZE", "APPDYNAMICS", "CHRONOSPHERE", "CLOUDWATCH", "DYNATRACE", "ELASTICSEARCH", "OPENSEARCH", "PROMETHEUS", "SPLUNK"],
    urlSlug: "telemetry",
  },
  {
    id: "source-control",
    icon: Code2,
    integrationTypes: ["GITHUB", "GITLAB", "BITBUCKET"],
    urlSlug: "source-control",
  },
  {
    id: "chat",
    icon: MessageCircleMore,
    integrationTypes: ["SLACK", "DISCORD"],
    urlSlug: "chat",
  },
  {
    id: "alert-response",
    icon: AlertTriangle,
    integrationTypes: ["PAGERDUTY", "OPSGENIE", "ROOTLY", "FIREHYDRANT", "INCIDENT_IO"],
    urlSlug: "alert-response",
  },
  {
    id: "documentation",
    icon: FileCheck,
    integrationTypes: ["NOTION", "CONFLUENCE"],
    urlSlug: "documentation",
  },
  {
    id: "cicd-workflows",
    icon: Workflow,
    integrationTypes: ["TEMPORAL", "BUILDKITE"],
    urlSlug: "cicd-workflows",
  },
  {
    id: "project-management",
    icon: ClipboardList,
    integrationTypes: ["JIRA", "LINEAR"],
    urlSlug: "project-management",
  },
  {
    id: "cloud-infrastructure",
    icon: Cloud,
    integrationTypes: ["AWS", "GCP"],
    urlSlug: "cloud-infrastructure",
  },
  {
    id: "data-warehouses",
    icon: Database,
    integrationTypes: ["SNOWFLAKE", "POSTGRESQL"],
    urlSlug: "data-warehouses",
  },
  {
    id: "security",
    icon: Shield,
    integrationTypes: ["SOCKET"],
    urlSlug: "security",
  },
  {
    id: "coding-agents",
    icon: Bot,
    integrationTypes: ["CURSOR", "DEVIN"],
    urlSlug: "coding-agents",
  },
  {
    id: "mcp-servers",
    icon: Server,
    integrationTypes: [],
    urlSlug: "mcp-servers",
  },
];

// Generate category title based on product name (some titles vary by brand)
function getCategoryTitle(categoryId: IntegrationCategoryId, productName: string): string {
  const isRootlyBranded = productName === "Rootly AI SRE";

  switch (categoryId) {
    case "telemetry":
      return "Telemetry & Observability";
    case "source-control":
      return "Code";
    case "chat":
      return "Chat (Slack or Discord)";
    case "alert-response":
      // Rootly branded orgs only have Alerts enabled, not Incidents
      return isRootlyBranded ? "Alert Response" : "Alert & Incident Response";
    case "documentation":
      return "Knowledge Base";
    case "cicd-workflows":
      return "CI/CD & Workflows";
    case "project-management":
      return "Project Management & Ticketing";
    case "cloud-infrastructure":
      return "Cloud Infrastructure";
    case "data-warehouses":
      return "Data Storage";
    case "security":
      return "Security";
    case "coding-agents":
      return "Coding Agents";
    case "mcp-servers":
      return "Custom MCP Servers";
    default:
      return "";
  }
}

// Generate category descriptions based on product name
function getCategoryDescription(categoryId: IntegrationCategoryId, productName: string): string {
  const isRootlyBranded = productName === "Rootly AI SRE";

  switch (categoryId) {
    case "telemetry":
      return `Connect Datadog, Sentry, LangSmith, Langfuse, or your telemetry data source so ${productName} can analyze logs, metrics, and traces.`;
    case "source-control":
      return `Connect ${productName} to GitHub, GitLab, or Bitbucket to analyze your code, commits, and pull requests.`;
    case "chat":
      return `Connect ${productName} to Slack or Discord for direct notifications and messaging capabilities.`;
    case "alert-response":
      // Rootly branded orgs only use Rootly for alert response
      return isRootlyBranded
        ? `Connect Rootly so ${productName} can listen and respond to your alerts.`
        : `Connect PagerDuty, OpsGenie, Rootly, or your on-call management provider so ${productName} can listen and respond to your alerts.`;
    case "documentation":
      return `Connect Notion and Confluence so ${productName} can ingest and query runbooks, SOPs, and other key documents.`;
    case "cicd-workflows":
      return `Connect Temporal or BuildKite so ${productName} can monitor your CI/CD pipelines and workflow orchestration.`;
    case "project-management":
      return `Connect Jira or Linear so ${productName} can read and search your project tickets and issues.`;
    case "cloud-infrastructure":
      return `Connect AWS or GCP to enable ${productName} to inspect resources and diagnose issues in your cloud environment.`;
    case "data-warehouses":
      return `Connect your databases and data warehouses so ${productName} can query and analyze your data.`;
    case "security":
      return `Connect your security scanners so ${productName} can surface vulnerabilities, supply chain risks, and cloud security posture issues.`;
    case "coding-agents":
      return `Connect coding agents like Cursor or Devin so ${productName} can write fixes with your preferred agent.`;
    case "mcp-servers":
      return `Connect external MCP servers to extend ${productName} with custom tools.`;
    default:
      return "";
  }
}

// Integration types allowed for Rootly branded orgs (whitelist)
export const ROOTLY_ALLOWED_INTEGRATIONS: IntegrationType[] = [
  "AWS",
  "BUILDKITE",
  "CLOUDWATCH",
  "CONFLUENCE",
  "DATADOG",
  "DISCORD",
  "GITHUB",
  "GRAFANA_CLOUD",
  "NEW_RELIC",
  "NOTION",
  "ROOTLY",
  "SENTRY",
  "SLACK",
  "TEMPORAL",
];

// Integration types that require superadmin access
export const SUPERADMIN_ONLY_INTEGRATIONS: IntegrationType[] = [];

export interface IntegrationInfo {
  name: string;
  description: string;
  available?: boolean;
}

export const INTEGRATION_INFO: Record<IntegrationType, IntegrationInfo> = {
  DATADOG: { name: "Datadog", description: "Monitor and analyze logs, metrics, and traces" },
  NEW_RELIC: { name: "New Relic", description: "Full-stack observability platform" },
  GRAFANA_CLOUD: { name: "Grafana", description: "Composable observability platform" },
  GCP_CLOUD_MONITORING: { name: "GCP Cloud Monitoring", description: "Google Cloud monitoring and observability" },
  SENTRY: { name: "Sentry", description: "Application monitoring and error tracking" },
  LANGSMITH: { name: "LangSmith", description: "LLM application observability and tracing" },
  LANGFUSE: { name: "Langfuse", description: "Open-source LLM observability and tracing" },
  APPDYNAMICS: { name: "AppDynamics", description: "Application performance monitoring", available: false },
  CHRONOSPHERE: { name: "Chronosphere", description: "Cloud-native observability platform" },
  CLOUDWATCH: { name: "AWS CloudWatch", description: "AWS monitoring and observability" },
  DYNATRACE: { name: "Dynatrace", description: "Software intelligence platform", available: false },
  ELASTICSEARCH: { name: "Elasticsearch", description: "Search and analyze logs from your Elasticsearch cluster" },
  OPENSEARCH: { name: "OpenSearch", description: "Search and analytics suite", available: false },
  PROMETHEUS: { name: "Prometheus", description: "Query and analyze metrics from your Prometheus server" },
  SPLUNK: { name: "Splunk", description: "Data platform for security and observability", available: false },
  BITBUCKET: { name: "Bitbucket", description: "Bitbucket code repository and CI/CD platform" },
  GITHUB: { name: "GitHub", description: "Code repository and collaboration platform" },
  GITLAB: { name: "GitLab", description: "GitLab code repository and CI/CD platform" },
  SLACK: { name: "Slack", description: "Team messaging and collaboration" },
  DISCORD: { name: "Discord", description: "Community chat platform" },
  PAGERDUTY: { name: "PagerDuty", description: "Incident management and on-call scheduling" },
  OPSGENIE: { name: "OpsGenie", description: "Alert and incident management" },
  ROLLBAR: { name: "Rollbar", description: "Error tracking and crash reporting" },
  ROOTLY: { name: "Rootly", description: "Incident management and response" },
  FIREHYDRANT: { name: "FireHydrant", description: "Incident management platform" },
  NOTION: { name: "Notion", description: "All-in-one workspace for notes and docs" },
  CONFLUENCE: { name: "Confluence", description: "Team collaboration and documentation" },
  JIRA: { name: "Jira", description: "Project management and issue tracking" },
  LINEAR: { name: "Linear", description: "Issue tracking and project management" },
  TEMPORAL: { name: "Temporal", description: "Workflow orchestration platform" },
  BUILDKITE: { name: "BuildKite", description: "CI/CD pipeline platform" },
  INCIDENT_IO: { name: "incident.io", description: "Incident management and response platform" },
  AWS: { name: "AWS", description: "Connect your AWS account for cloud monitoring." },
  GCP: { name: "GCP", description: "Connect your GCP account for cloud monitoring." },
  SNOWFLAKE: { name: "Snowflake", description: "Query and inspect your Snowflake data warehouse" },
  PGANALYZE: { name: "PGAnalyze", description: "PostgreSQL performance monitoring and query optimization" },
  POSTGRESQL: { name: "PostgreSQL", description: "Query and inspect your PostgreSQL databases" },
  SOCKET: { name: "Socket", description: "Supply chain security and dependency reachability analysis" },
  CURSOR: { name: "Cursor", description: "Delegate code changes to Cursor's Cloud Agents via direct API" },
  DEVIN: { name: "Devin", description: "Delegate code changes to Devin (Cognition) via direct API" },
};

// Get integration categories with titles and descriptions for the given product name
export function getIntegrationCategories(productName: string = "TierZero", brandTheme?: string | null, isSuperAdmin: boolean = false): IntegrationCategory[] {
  const isRootlyBranded = productName === "Rootly AI SRE";
  const shouldApplyWhitelist = isRootlyBranded && brandTheme !== "ROOTLY_HYBRID";

  return BASE_INTEGRATION_CATEGORIES.map(cat => {
    // Filter out unavailable integrations, superadmin-only integrations for non-superadmins,
    // and only allow whitelisted integrations for ROOTLY/ROOTLY_COBRAND orgs
    const integrationTypes = cat.integrationTypes.filter(type => {
      if (INTEGRATION_INFO[type]?.available === false) return false;
      if (!isSuperAdmin && SUPERADMIN_ONLY_INTEGRATIONS.includes(type)) return false;
      if (shouldApplyWhitelist && !ROOTLY_ALLOWED_INTEGRATIONS.includes(type)) return false;
      return true;
    });

    return {
      ...cat,
      integrationTypes,
      title: getCategoryTitle(cat.id, productName),
      description: getCategoryDescription(cat.id, productName),
    };
  }).filter(cat => cat.integrationTypes.length > 0 || cat.id === "mcp-servers");
}

// Static list of all categories (includes superadmin-only integrations) — used for routing and slug lookup
// eslint-disable-next-line custom/no-hardcoded-brand-name -- static routing table, not user-facing
export const INTEGRATION_CATEGORIES: IntegrationCategory[] = getIntegrationCategories("TierZero", null, true);

export function getCategoryBySlug(slug: string): IntegrationCategory | undefined {
  return INTEGRATION_CATEGORIES.find(cat => cat.urlSlug === slug);
}

export function calculateProgress(connectedTypes: string[]): number {
  const connectedCategories = INTEGRATION_CATEGORIES.filter(cat =>
    cat.integrationTypes.some(type => connectedTypes.includes(type))
  ).length;
  return Math.round((connectedCategories / INTEGRATION_CATEGORIES.length) * 100);
}
